import { useEffect, useRef, useSyncExternalStore } from 'react'
import { useAppStore } from '@core/store'
import { viewerApi } from './lib/viewerApi'
import { parseDeepLink, type DeepLink } from './lib/deepLink'
import { loadSampleIfc, SAMPLE_IFCS } from './lib/loadSampleIfc'

// ---------------------------------------------------------------------------
// DeepLinkLoader: componente sem UI (renderiza null), montado UMA vez no
// PopupsHost. No arranque lê window.location.search: se houver uma ligação
// partilhável (?sample=… ou ?ifc=https://…), espera que o loader do viewer
// esteja pronto e dispara o carregamento UMA única vez. A guarda de useRef
// impede que re-renders ou trocas de modelo posteriores voltem a disparar.
// O parâmetro fica na URL de propósito (é a ligação partilhável).
//
// Nota lint: evitamos setState de useState dentro de useEffect. Toda a
// coordenação vive em refs; os avisos ao utilizador usam pushToast do
// store (não é estado local de React).
// ---------------------------------------------------------------------------

/** Deriva um nome de ficheiro do último segmento do path da URL
 *  (fallback ao valor dado). */
function fileNameFromUrl(url: string, fallback = 'modelo.ifc'): string {
  try {
    const u = new URL(url)
    const seg = u.pathname.split('/').filter(Boolean).pop()
    return seg && seg.length > 0 ? decodeURIComponent(seg) : fallback
  } catch {
    return fallback
  }
}

/** Depois do modelo carregar, se a ligação trouxer um `?ids=`, descarrega
 *  o ficheiro IDS e empurra-o para o painel de validação (aberto, pronto a
 *  avaliar). Não corre a avaliação automaticamente: é pesada e o
 *  utilizador clica "Validar". Falha aqui é não-fatal (o modelo já
 *  carregou), por isso o aviso não é persistente. */
async function loadIdsFromLink(idsUrl: string): Promise<void> {
  const { setIdsPrefill, setIdsPanelOpen, pushToast } = useAppStore.getState()
  let text: string
  try {
    const res = await fetch(idsUrl)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    text = await res.text()
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    pushToast(`Não foi possível abrir o IDS da ligação: ${msg}`, {
      type: 'error',
    })
    return
  }
  setIdsPrefill({ xml: text, name: fileNameFromUrl(idsUrl, 'regras.ids') })
  setIdsPanelOpen(true)
  pushToast('Ficheiro IDS da ligação aberto', { type: 'success' })
}

async function attemptDeepLink(link: DeepLink): Promise<void> {
  if (link.kind === 'sample') {
    const sample = SAMPLE_IFCS.find((s) => s.id === link.id)
    if (!sample) return
    await loadSampleIfc(sample, { replace: true })
  } else {
    let res: Response
    try {
      res = await fetch(link.url)
    } catch {
      throw new Error('não foi possível descarregar o ficheiro.')
    }
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`)
    }
    const ab = await res.arrayBuffer()
    const file = new File([ab], fileNameFromUrl(link.url), {
      type: 'application/octet-stream',
    })
    const aberto = await viewerApi.loadIfcFile(file, { replace: true })
    // O ficheiro chegou mas não abre: tentar outra vez não muda nada, e a
    // mensagem tem de ser a do carregador, não "foi descartado".
    if (!aberto.ok) throw new FalhaDefinitiva(aberto.erro)
  }
}

/** Uma falha que não se resolve a tentar outra vez (o ficheiro não abre). */
class FalhaDefinitiva extends Error {}

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms))

async function runDeepLink(link: DeepLink): Promise<void> {
  const { pushToast } = useAppStore.getState()
  // Até 3 tentativas COM VERIFICAÇÃO no store: o deep-link dispara no
  // instante em que o loader fica pronto, tão cedo no arranque que o
  // mundo 3D ainda pode ser remontado a seguir (em dev o StrictMode
  // garante-o; observado também em produção) e o modelo carregado é
  // varrido sem qualquer erro. Um "await" bem-sucedido não chega:
  // confirmamos que o modelo FICOU na lista, senão repetimos. Também
  // cobre falhas transitórias do download (raw.githubusercontent).
  let lastErr: unknown = null
  for (let attempt = 1; attempt <= 3; attempt++) {
    if (attempt > 1) await wait(1500)
    try {
      await attemptDeepLink(link)
    } catch (err) {
      lastErr = err
      if (err instanceof FalhaDefinitiva) break
      continue
    }
    // Janela de assentamento: se uma remontagem do mundo varrer o
    // modelo, acontece logo a seguir ao load.
    await wait(1200)
    if (useAppStore.getState().models.length > 0) {
      pushToast('Modelo da ligação carregado', { type: 'success' })
      // O modelo assentou: se a ligação trouxer regras IDS, abre-as agora.
      if (link.ids) await loadIdsFromLink(link.ids)
      return
    }
    lastErr = new Error('o modelo foi descartado durante o arranque.')
  }
  const msg = lastErr instanceof Error ? lastErr.message : String(lastErr)
  // Persistente (timeout 0): quem abriu a ligação tem de ver porque é
  // que o viewer ficou vazio, mesmo que olhe segundos depois.
  pushToast(`Falha a carregar o modelo da ligação: ${msg}`, {
    type: 'error',
    timeout: 0,
  })
}

function DeepLinkLoader() {
  const loaderReady = useSyncExternalStore(
    viewerApi.subscribe,
    viewerApi.hasIfcLoader,
  )
  // Parse à cabeça, uma vez (undefined = ainda não corrido).
  const linkRef = useRef<DeepLink | null | undefined>(undefined)
  if (linkRef.current === undefined) {
    linkRef.current = parseDeepLink(window.location.search)
  }
  const firedRef = useRef(false)

  useEffect(() => {
    const link = linkRef.current
    if (!link || !loaderReady || firedRef.current) return
    firedRef.current = true
    void runDeepLink(link)
  }, [loaderReady])

  return null
}

export default DeepLinkLoader
